import { GeckoEntryBase } from 'types/entries/geckoEntry'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/entries/rolodexEntryFields'
import { EntryIndexType } from 'types/helpers'
import { parseDate, parseString } from 'utils/parsingHelpers'

import { EODNote, parseEODNote } from './eodNotes'
import {
  EquipmentDetailJSON,
  parseEquipmentDetailJSON,
} from './equipmentDetails'
import {
  parsePersonnelDetailsJSON,
  PersonnelDetailsJSON,
} from './personnelDetails'
import { parseSafetyJSON, SafetyJSON } from './safety'
import { parseScopeJSON, ScopeJSON } from './scope'
import { parseSiteDetailsJSON, SiteDetailsJSON } from './siteDetails'

export type JobEntry = {
  entry: {
    id: string
    type: RolodexEntryType
    data?: GeckoJobDataJSON
    generation?: number | null | undefined
    index?: { [key: string]: unknown } | null
    tags?: { [key: string]: string | null }
  }
}

export enum JobStatus {
  Scheduled = 'scheduled',
  InProgress = 'in_progress',
  Completed = 'completed',
  Canceled = 'canceled',
}

export type GeckoJobDataJSON = {
  //old fields
  start_date: Date
  end_date: Date
  status?: string
  qa_qc_lead?: string
  ops_data_lead?: string
  supervisor?: string
  data_lead?: string
  sales_rep?: string
  //new fields
  name?: string
  scope?: ScopeJSON
  personnel_details?: PersonnelDetailsJSON
  safety?: SafetyJSON
  site_details?: SiteDetailsJSON
  equipment_details?: EquipmentDetailJSON[]
  eod_notes?: EODNote[]
  assigned_equipment_configurations?: string[]
}

export class GeckoJob extends GeckoEntryBase<
  GeckoJobDataJSON,
  {
    organization: string
    site: string
    [RolodexEntryType.SALESFORCE_JOB]: string
  },
  EntryIndexType
> {
  public static CreateFromJSON(json: RolodexEntryJson) {
    return createGeckoJobFromJson(json)
  }
}

export function createGeckoJobFromJson(json: RolodexEntryJson): GeckoJob {
  return {
    id: json.id,
    data: {
      name: parseString(json.data?.name),
      start_date: parseDate(json.data?.start_date) ?? new Date(0),
      end_date: parseDate(json.data?.end_date) ?? new Date(0),
      status: parseString(json.data?.status),
      qa_qc_lead: parseString(json.data?.qa_qc_lead),
      ops_data_lead: parseString(json.data?.ops_data_lead),
      supervisor: parseString(json.data?.supervisor),
      data_lead: parseString(json.data?.data_lead),
      sales_rep: parseString(json.data?.sales_rep),
      scope: parseScopeJSON(json.data?.scope),
      personnel_details: parsePersonnelDetailsJSON(
        json.data?.personnel_details
      ),
      safety: parseSafetyJSON(json.data?.safety),
      site_details: parseSiteDetailsJSON(json.data?.site_details),
      equipment_details: Array.isArray(json.data?.equipment_details)
        ? json.data.equipment_details
            .map(parseEquipmentDetailJSON)
            .filter((item): item is EquipmentDetailJSON => item !== undefined)
        : undefined,
      eod_notes: Array.isArray(json.data?.eod_notes)
        ? json.data.eod_notes
            .map(parseEODNote)
            .filter((item): item is EODNote => item !== undefined)
        : undefined,
      assigned_equipment_configurations: Array.isArray(
        json.data?.assigned_equipment_configurations
      )
        ? json.data.assigned_equipment_configurations
        : [],
    },
    tags: {
      organization: parseString(json.tags?.organization) || '',
      site: parseString(json.tags?.site) || '',
      [RolodexEntryType.SALESFORCE_JOB]:
        parseString(json.tags?.[RolodexEntryType.SALESFORCE_JOB]) || '',
    },
    type: json.type,
    generation: json.generation,
    updated_at: json.updated_at,
    view_restriction: json.view_restriction === 'internal' ? 'internal' : null,
    isFromCache: false,
    index: json.index ?? {},
    raw: json,
  }
}
